import { Subject, Observable, ReplaySubject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { Breakpoints, BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, OnDestroy } from '@angular/core';

import {
  isComparisonString,
  isLtComparisonString,
  isLteComparisonString,
  isGteComparisonString,
  isGtComparisonString,
} from '../helpers';

export enum SnBreakpoint {
  XSmall = 'xs',
  Small = 'sm',
  Medium = 'md',
  Large = 'lg',
  XLarge = 'xl',
}

@Injectable({ providedIn: 'root' })
export class SnMediaService implements OnDestroy {
  private _breakpoint: SnBreakpoint;
  private readonly _breakpointChange$ = new ReplaySubject<SnBreakpoint>(10);

  private readonly _breakpointList: string[] = [
    SnBreakpoint.XSmall,
    SnBreakpoint.Small,
    SnBreakpoint.Medium,
    SnBreakpoint.Large,
    SnBreakpoint.XLarge,
  ];

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _breakpointObserver: BreakpointObserver) {
    this._updateBreakpoint();
    this._initBreakpointSubscriptions();
  }

  get breakpoint(): SnBreakpoint {
    return this._breakpoint;
  }

  get breakpointChange$(): Observable<SnBreakpoint> {
    return this._breakpointChange$.asObservable();
  }

  public ngOnDestroy(): void {
    this._breakpointChange$.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public isActive(...breakpoints: string[]): boolean {
    return breakpoints.some((breakpoint) => this._isActiveBreakpoint(breakpoint));
  }

  private _isActiveBreakpoint(value: string): boolean {
    const currentBreakpointIndex = this._breakpointList.indexOf(this.breakpoint);

    if (isComparisonString(value)) {
      const breakpoint = value.split('-')[1];
      const breakpointIndex = this._breakpointList.indexOf(breakpoint);

      if (breakpointIndex === -1) {
        return false;
      } else if (isLteComparisonString(value)) {
        return currentBreakpointIndex <= breakpointIndex;
      } else if (isLtComparisonString(value)) {
        return currentBreakpointIndex < breakpointIndex;
      } else if (isGteComparisonString(value)) {
        return currentBreakpointIndex >= breakpointIndex;
      } else if (isGtComparisonString(value)) {
        return currentBreakpointIndex > breakpointIndex;
      }
    }

    return this.breakpoint === value;
  }

  private _initBreakpointSubscriptions(): void {
    this._breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(
        filter((state) => state.matches),
        takeUntil(this._destroy$),
      )
      .subscribe(() => this._updateBreakpoint());
  }

  private _updateBreakpoint(): void {
    if (this._breakpointObserver.isMatched(Breakpoints.XLarge)) {
      this._setBreakpoint(SnBreakpoint.XLarge);
    } else if (this._breakpointObserver.isMatched(Breakpoints.Large)) {
      this._setBreakpoint(SnBreakpoint.Large);
    } else if (this._breakpointObserver.isMatched(Breakpoints.Medium)) {
      this._setBreakpoint(SnBreakpoint.Medium);
    } else if (this._breakpointObserver.isMatched(Breakpoints.Small)) {
      this._setBreakpoint(SnBreakpoint.Small);
    } else {
      this._setBreakpoint(SnBreakpoint.XSmall);
    }
  }

  private _setBreakpoint(breakpoint: SnBreakpoint): void {
    if (this._breakpoint !== breakpoint) {
      this._breakpoint = breakpoint;
      this._breakpointChange$.next(breakpoint);
    }
  }
}
